import * as React from 'react'

import {
  Box,
  CheckBox,
  Icon,
  MediumIcon,
  Tag,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'

import UserAvatar from 'modules/core/components/UserAvatar'
import i18n from 'modules/core/lib/i18n'
import type User from 'modules/core/store/models/User'

export type GetUserExtraInformationFn = (
  accessId: number | null | undefined,
  disabled?: boolean
) => UserExtraInformation | undefined

interface UserExtraInformation {
  label: string
  icon: MediumIcon
}

type UserProps = UserProfileProps & {
  selected: boolean
}
interface UserProfileProps {
  user: User
  suggested?: boolean
  confirmed?: boolean
  disabled?: boolean
  onGetUserExtraInformation?: GetUserExtraInformationFn
}

const UserProfile = ({
  suggested,
  confirmed,
  user,
  disabled,
  onGetUserExtraInformation,
}: UserProfileProps) => {
  const userExtraInformation = onGetUserExtraInformation
    ? onGetUserExtraInformation(user.attributes.id, disabled)
    : null

  const [showUserExtraInformation, toggleUserExtraInformation] = useToggle(false)

  return (
    <Box alignItems="center" flexDirection="row" flexGrow gap="s12">
      <Box flexNoShrink style={{ opacity: disabled ? '0.5' : '1' }}>
        <UserAvatar user={user} size="s24" />
      </Box>
      <Box flexDirection="row" justifyContent="spaceBetween" width="full" textAlign="left">
        <Box
          style={{
            maxWidth: '160',
          }}
        >
          <Text.Normal weight="medium" ellipsis color={disabled ? 'grey600' : 'grey800'}>
            {user.fullName()}
          </Text.Normal>
        </Box>

        {suggested && (
          <Box>
            <Tag type="champagne">{i18n.t('split_documents.assign_documents.suggested')}</Tag>
          </Box>
        )}
        {confirmed && (
          <Box>
            <Tag type="viridian">{i18n.t('split_documents.assign_documents.match')}</Tag>
          </Box>
        )}
        {userExtraInformation && (
          <Tooltip.Alt
            attachment="top center"
            open={showUserExtraInformation}
            toggle={toggleUserExtraInformation}
            label={userExtraInformation.label}
          >
            <Icon.Medium icon={userExtraInformation.icon} color="grey600" />
          </Tooltip.Alt>
        )}
      </Box>
    </Box>
  )
}

const SingleSelectUser = ({
  suggested,
  confirmed,
  user,
  selected,
  disabled,
  onGetUserExtraInformation,
}: UserProps) => {
  return (
    <Box
      alignItems="center"
      background={selected ? 'grey200' : 'grey000White'}
      cursor="pointer"
      flexDirection="row"
      gap="s16"
      paddingX="s16"
      paddingY="s12"
      width="full"
    >
      <UserProfile
        suggested={suggested}
        confirmed={confirmed}
        user={user}
        disabled={disabled}
        onGetUserExtraInformation={onGetUserExtraInformation}
      />
      {selected && (
        <Box>
          <Icon.Medium icon="check" color="grey800" />
        </Box>
      )}
    </Box>
  )
}

const MultiSelectUser = ({
  suggested,
  confirmed,
  user,
  selected,
  disabled,
  onGetUserExtraInformation,
}: UserProps) => {
  return (
    <Box
      alignItems="center"
      background="grey000White"
      cursor="pointer"
      flexDirection="row"
      gap="s16"
      paddingX="s16"
      paddingY="s12"
      width="full"
    >
      <Box>
        <CheckBox checked={selected} disabled={disabled} />
      </Box>
      <UserProfile
        suggested={suggested}
        confirmed={confirmed}
        user={user}
        disabled={disabled}
        onGetUserExtraInformation={onGetUserExtraInformation}
      />
    </Box>
  )
}

interface Props {
  suggested: boolean
  confirmed: boolean
  user: User
  selected: boolean
  disabled: boolean
  onSelectUser: (user: User) => () => void
  isMultiSelect: boolean
  onGetUserExtraInformation?: GetUserExtraInformationFn
}

export default function Item({ isMultiSelect, ...params }: Props) {
  return (
    <Box onClick={params.disabled ? null : params.onSelectUser(params.user)} width="full">
      {isMultiSelect ? <MultiSelectUser {...params} /> : <SingleSelectUser {...params} />}
    </Box>
  )
}
